import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FaBalanceScale,
  FaCamera,
  FaChevronDown,
  FaChevronUp,
  FaClock,
  FaExclamationCircle,
  FaInfoCircle,
  FaReceipt,
  FaShieldAlt,
  FaSpinner,
  FaUpload,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { disputeSellerService, resolveProductMediaUrl } from '../../../../services/apiService';

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function formatWhen(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function statusTone(chip = '', status = '') {
  const key = String(chip || status).toLowerCase();
  if (key.includes('evidence')) {
    return {
      badge: 'badge-info',
      accent: 'border-l-sky-500',
      dot: 'bg-sky-500',
    };
  }
  if (key.includes('review') || key.includes('open') || key === 'admin reviewing') {
    return {
      badge: 'badge-neutral',
      accent: 'border-l-indigo-500',
      dot: 'bg-indigo-500',
    };
  }
  if (key.includes('refund') || key.includes('resolved')) {
    return {
      badge: 'badge-success',
      accent: 'border-l-emerald-500',
      dot: 'bg-emerald-500',
    };
  }
  if (key.includes('reject') || key.includes('no refund')) {
    return {
      badge: 'badge-danger',
      accent: 'border-l-red-500',
      dot: 'bg-red-500',
    };
  }
  return {
    badge: 'badge-neutral',
    accent: 'border-l-slate-400',
    dot: 'bg-slate-400',
  };
}

function scrollDashboardToTop() {
  document.querySelector('.dashboard-main')?.scrollTo({ top: 0, behavior: 'smooth' });
}

function StatusBadge({ label, status }) {
  const tone = statusTone(label, status);
  return (
    <span className={`inline-flex items-center gap-1.5 ${tone.badge}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} aria-hidden />
      {label}
    </span>
  );
}

export default function VendorDisputes() {
  const [tab, setTab] = useState('active');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [disputes, setDisputes] = useState([]);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [evidenceNotes, setEvidenceNotes] = useState({});
  const autoExpandedRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await disputeSellerService.listDisputes(tab);
      setDisputes(Array.isArray(rows) ? rows : []);
    } catch (err) {
      toast.error(err.message || 'Failed to load disputes');
      setDisputes([]);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  const openDetail = useCallback(
    async (orderId) => {
      if (expandedOrderId === orderId) {
        setExpandedOrderId(null);
        setDetail(null);
        return;
      }
      setExpandedOrderId(orderId);
      setDetailLoading(true);
      try {
        const res = await disputeSellerService.getDispute(orderId);
        setDetail(res?.dispute || res);
      } catch (err) {
        toast.error(err.message || 'Could not load dispute');
        setDetail(null);
      } finally {
        setDetailLoading(false);
      }
    },
    [expandedOrderId]
  );

  useEffect(() => {
    load();
    scrollDashboardToTop();
  }, [load]);

  useEffect(() => {
    autoExpandedRef.current = false;
  }, [tab]);

  useEffect(() => {
    if (loading || tab !== 'active' || disputes.length !== 1 || autoExpandedRef.current) return;
    autoExpandedRef.current = true;
    openDetail(disputes[0].orderId);
  }, [loading, disputes, tab, openDetail]);

  const handleEvidenceUpload = async (orderId, fileList) => {
    const files = Array.from(fileList || []).slice(0, 3);
    if (!files.length) return;
    const encoded = [];
    for (const file of files) {
      encoded.push(await readFileAsDataUrl(file));
    }
    setSubmitting(true);
    try {
      await disputeSellerService.submitEvidenceResponse(orderId, {
        photos: encoded,
        description:
          (evidenceNotes[orderId] || '').trim() ||
          'Seller evidence submitted per admin request',
      });
      toast.success('Evidence submitted');
      setEvidenceNotes((prev) => ({ ...prev, [orderId]: '' }));
      await load();
      if (expandedOrderId === orderId) {
        const res = await disputeSellerService.getDispute(orderId);
        setDetail(res?.dispute || res);
      }
    } catch (err) {
      toast.error(err.message || 'Could not submit evidence');
    } finally {
      setSubmitting(false);
    }
  };

  const tabs = useMemo(
    () => [
      { id: 'active', label: 'Active', hint: 'Open claims under review' },
      { id: 'evidence', label: 'Evidence due', hint: 'Action required from you' },
      { id: 'resolved', label: 'Resolved', hint: 'Closed claims' },
    ],
    []
  );

  return (
    <div className="vd-disputes rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm sm:p-6">
      <div className="dashboard-page-header !mb-5">
        <div>
          <h2 className="dashboard-page-title flex items-center gap-2.5">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-100 text-indigo-600">
              <FaBalanceScale className="text-lg" />
            </span>
            Disputes &amp; claims
          </h2>
          <p className="dashboard-page-desc max-w-2xl">
            Review buyer claims on your orders. When admin requests proof, upload dispatch or
            packaging photos within <strong className="font-semibold text-slate-700">48 hours</strong>.
          </p>
        </div>
      </div>

      <div className="sticky top-0 z-10 -mx-5 mb-5 border-b border-slate-100 bg-white/95 px-5 py-3 backdrop-blur-sm sm:-mx-6 sm:px-6">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => {
                setTab(t.id);
                setExpandedOrderId(null);
                setDetail(null);
                scrollDashboardToTop();
              }}
              className={`rounded-xl border px-4 py-3 text-left transition-all ${
                tab === t.id
                  ? 'border-indigo-200 bg-indigo-50 shadow-sm ring-1 ring-indigo-100'
                  : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              <p
                className={`text-sm font-bold ${
                  tab === t.id ? 'text-indigo-700' : 'text-slate-900'
                }`}
              >
                {t.label}
              </p>
              <p className="mt-0.5 text-xs text-slate-500">{t.hint}</p>
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-gray-200 bg-white py-16 text-gray-500">
          <FaSpinner className="animate-spin text-2xl text-orange-500" />
          <p className="text-sm">Loading disputes…</p>
        </div>
      ) : disputes.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50/80 px-6 py-14 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-gray-200">
            <FaShieldAlt className="text-xl text-gray-400" />
          </div>
          <p className="text-base font-semibold text-gray-900">No disputes in this category</p>
          <p className="mx-auto mt-2 max-w-sm text-sm text-gray-500">
            {tab === 'active'
              ? 'When a buyer raises a claim on your order, it will appear here.'
              : tab === 'evidence'
                ? 'Claims needing your photos or notes will show up here.'
                : 'Closed and settled claims will be listed here for your records.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {disputes.map((row) => {
            const expanded = expandedOrderId === row.orderId;
            const tone = statusTone(row.statusChip, row.status);
            const showEvidence =
              row.sellerEvidenceDue ||
              (row.pendingSellerEvidence &&
                row.status === 'evidence_requested' &&
                !row.pendingSellerEvidence.submittedAt);

            return (
              <article
                key={row.claimId || row.orderId}
                className={`overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md border-l-4 ${tone.accent}`}
              >
                <button
                  type="button"
                  onClick={() => openDetail(row.orderId)}
                  className="flex w-full items-start justify-between gap-4 px-4 py-4 text-left sm:px-5 sm:py-5"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-md bg-slate-100 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                        {row.claimId}
                      </span>
                      {showEvidence && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-blue-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                          <FaExclamationCircle className="text-[9px]" />
                          Action needed
                        </span>
                      )}
                    </div>

                    <h3 className="mt-2 text-base font-bold leading-snug text-gray-900 sm:text-lg">
                      {row.partName || row.orderId}
                    </h3>

                    <p className="mt-1 text-sm text-gray-600">
                      {row.issueLabel || row.issueType}
                    </p>

                    <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-gray-500">
                      <span className="inline-flex items-center gap-1.5">
                        <FaReceipt className="text-gray-400" />
                        Order {row.orderId}
                      </span>
                      {row.submittedAt && (
                        <span className="inline-flex items-center gap-1.5">
                          <FaClock className="text-gray-400" />
                          Filed {formatWhen(row.submittedAt)}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex shrink-0 flex-col items-end gap-2">
                    <StatusBadge label={row.statusChip || row.status} status={row.status} />
                    {expanded ? (
                      <FaChevronUp className="text-gray-400" />
                    ) : (
                      <FaChevronDown className="text-gray-400" />
                    )}
                  </div>
                </button>

                {expanded && (
                  <div className="border-t border-gray-100 bg-gradient-to-b from-slate-50/90 to-white px-4 py-5 sm:px-5">
                    {detailLoading ? (
                      <div className="flex items-center justify-center gap-2 py-8 text-sm text-gray-500">
                        <FaSpinner className="animate-spin text-orange-500" />
                        Loading claim details…
                      </div>
                    ) : detail?.orderId === row.orderId ? (
                      <div className="space-y-4">
                        <div className="grid gap-3 sm:grid-cols-3">
                          <div className="rounded-xl border border-gray-200 bg-white p-3">
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                              Claim ID
                            </p>
                            <p className="mt-1 font-mono text-xs font-semibold text-gray-900">
                              {detail.claimId}
                            </p>
                          </div>
                          <div className="rounded-xl border border-gray-200 bg-white p-3">
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                              Order
                            </p>
                            <p className="mt-1 text-xs font-semibold text-gray-900">{detail.orderId}</p>
                          </div>
                          <div className="rounded-xl border border-gray-200 bg-white p-3">
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                              Filed on
                            </p>
                            <p className="mt-1 text-xs font-semibold text-gray-900">
                              {formatWhen(detail.submittedAt)}
                            </p>
                          </div>
                        </div>

                        <div className="rounded-xl border border-gray-200 bg-white p-4">
                          <p className="text-xs font-bold uppercase tracking-wider text-gray-500">
                            Buyer&apos;s claim
                          </p>
                          <p className="mt-2 text-sm leading-relaxed text-gray-800">
                            {detail.description || '—'}
                          </p>
                        </div>

                        {(detail.photos || []).length > 0 && (
                          <div>
                            <p className="mb-2 text-xs font-bold uppercase tracking-wider text-gray-500">
                              Buyer photos
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {detail.photos.map((photo, idx) => (
                                <a
                                  key={idx}
                                  href={resolveProductMediaUrl(photo.url || photo)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="group block overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition hover:ring-2 hover:ring-orange-200"
                                >
                                  <img
                                    src={resolveProductMediaUrl(photo.url || photo)}
                                    alt={`Evidence ${idx + 1}`}
                                    className="h-20 w-20 object-cover transition group-hover:scale-105 sm:h-24 sm:w-24"
                                  />
                                </a>
                              ))}
                            </div>
                          </div>
                        )}

                        {showEvidence && detail.pendingSellerEvidence ? (
                          <div className="rounded-xl border-2 border-blue-200 bg-blue-50/80 p-4 sm:p-5">
                            <div className="flex items-start gap-3">
                              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-600 text-white">
                                <FaUpload className="text-sm" />
                              </span>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-bold text-blue-950">
                                  Admin requested your evidence
                                </p>
                                {detail.pendingSellerEvidence.responseDeadline && (
                                  <p className="mt-1 text-xs text-blue-800">
                                    Respond by{' '}
                                    <strong>
                                      {formatWhen(detail.pendingSellerEvidence.responseDeadline)}
                                    </strong>
                                  </p>
                                )}
                                {detail.pendingSellerEvidence.description && (
                                  <p className="mt-2 rounded-lg bg-white/70 px-3 py-2 text-xs text-blue-900">
                                    {detail.pendingSellerEvidence.description}
                                  </p>
                                )}
                              </div>
                            </div>

                            <textarea
                              value={evidenceNotes[row.orderId] || ''}
                              onChange={(e) =>
                                setEvidenceNotes((prev) => ({
                                  ...prev,
                                  [row.orderId]: e.target.value,
                                }))
                              }
                              rows={3}
                              placeholder="Explain your side — dispatch photo, packaging, part label, invoice, etc."
                              className="mt-4 w-full rounded-xl border border-blue-200 bg-white px-3 py-2.5 text-sm text-gray-800 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                            />

                            <label className="mt-3 inline-flex cursor-pointer items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-60">
                              <FaCamera />
                              {submitting ? 'Uploading…' : 'Upload evidence photos'}
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                className="hidden"
                                disabled={submitting}
                                onChange={(e) => {
                                  handleEvidenceUpload(row.orderId, e.target.files);
                                  e.target.value = '';
                                }}
                              />
                            </label>
                          </div>
                        ) : (
                          <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50/70 px-4 py-3">
                            <FaInfoCircle className="mt-0.5 shrink-0 text-emerald-600" />
                            <div>
                              <p className="text-sm font-semibold text-emerald-900">
                                No action needed from you right now
                              </p>
                              <p className="mt-1 text-xs leading-relaxed text-emerald-800">
                                Admin is reviewing this claim. You&apos;ll be notified here if
                                photos or documents are required.
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
